import { ApiService } from '@/support/ApiService';
import type { components } from '@/generated/internal-api/openapi-types';

type AgentSessionApiId = components['schemas']['AgentSessionApiId.schema'];
type StudyApiId = components['schemas']['StudyApiId.schema'];
type SendValidatorUploadRequestV1 = components['schemas']['SendValidatorUploadRequestV1.schema'];
type SendValidatorCreateRequestV1 = components['schemas']['SendValidatorCreateRequestV1.schema'];
type SendValidatorConfirmDomainRequestV1 = components['schemas']['SendValidatorConfirmDomainRequestV1.schema'];
type StudyProtocolParserUploadRequestV1 = components['schemas']['StudyProtocolParserUploadRequestV1.schema'];
type StudyProtocolParserCreateRequestV1 = components['schemas']['StudyProtocolParserCreateRequestV1.schema'];

/**
 * Thin client for the agent kickoff/cancel/get endpoints exposed by the
 * internal API. The streaming side runs over SSE — see AgentSessionStreamService.
 */
export class AgentSessionService {
  static createStudyHealthCheckSession(body: { study_api_id: StudyApiId }, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/study-health-check',
      body,
      signal,
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'agent-session-create-study-health-check' },
      },
    });
  }

  static createStudyProtocolParserSession(body: StudyProtocolParserCreateRequestV1, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/study-protocol-parser',
      body,
      signal,
      options: {
        onError: {
          toast: false,
          throw: false,
          capture: true,
          slug: 'agent-session-create-study-protocol-parser',
        },
      },
    });
  }

  static presignStudyProtocolParserUpload(body: StudyProtocolParserUploadRequestV1, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/study-protocol-parser/uploads',
      body,
      signal,
      options: {
        onError: {
          toast: false,
          throw: false,
          capture: true,
          slug: 'agent-session-presign-study-protocol-parser-upload',
        },
      },
    });
  }

  static createSendValidatorSession(body: SendValidatorCreateRequestV1, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/send-validator',
      body,
      signal,
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'agent-session-create-send-validator' },
      },
    });
  }

  static presignSendValidatorUpload(body: SendValidatorUploadRequestV1, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/send-validator/uploads',
      body,
      signal,
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'agent-session-presign-send-validator-upload' },
      },
    });
  }

  static confirmSendValidatorDomain(
    sessionApiId: AgentSessionApiId,
    body: SendValidatorConfirmDomainRequestV1,
    signal?: AbortSignal
  ) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/send-validator/{sessionApiId}/confirm-domain',
      path: { sessionApiId },
      body,
      signal,
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'agent-session-confirm-send-validator-domain' },
      },
    });
  }

  static getSession(sessionApiId: AgentSessionApiId, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}',
      path: { sessionApiId },
      signal,
      options: { onError: { toast: false, throw: false, capture: true, slug: 'agent-session-get' } },
    });
  }

  static getSendValidatorDomainConfirmation(sessionApiId: AgentSessionApiId, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'GET /api/v1/ai-agents/send-validator/{sessionApiId}/domain-confirmation',
      path: { sessionApiId },
      signal,
      options: {
        onError: {
          toast: false,
          throw: false,
          capture: true,
          slug: 'agent-session-get-send-validator-domain-confirmation',
        },
      },
    });
  }

  static cancelSession(sessionApiId: AgentSessionApiId, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/sessions/{sessionApiId}/cancel',
      path: { sessionApiId },
      signal,
      options: { onError: { toast: false, throw: false, capture: true, slug: 'agent-session-cancel' } },
    });
  }
}
